import { OrderStatus } from '@lombok/shared';
import type { OrderStatus as OrderStatusType } from '@lombok/shared';
import type { AgingBucket } from '@lombok/api-client';

/**
 * Spanish labels + ordering for the Reportes slice. Stored values stay snake_case
 * (from @lombok/shared / the reports API); only what users see is Spanish. Status
 * pill colors/labels live in StatusPill (shared map) — these are the report-specific
 * extras: the AR aging bucket labels (+ their fixed display order) and the
 * orders-by-status row order/labels for the "Ventas por estado" panel.
 */

/** AR aging bucket → short Spanish label for the buckets summary + table column. */
export const AGING_BUCKET_LABELS_ES: Record<AgingBucket, string> = {
  current: 'Corriente',
  d1_30: '1–30 días',
  d31_60: '31–60 días',
  d60_plus: '+60 días',
};

/** Fixed display order for the four aging buckets (oldest debt last). */
export const AGING_BUCKET_ORDER: readonly AgingBucket[] = [
  'current',
  'd1_30',
  'd31_60',
  'd60_plus',
];

/** Resolve an aging bucket to its Spanish label (raw value as fallback). */
export function agingBucketLabel(bucket: string): string {
  return AGING_BUCKET_LABELS_ES[bucket as AgingBucket] ?? bucket;
}

/**
 * How far an open invoice is from its due date, in words. `ageDays` is days past due, so it is
 * negative while the invoice is not yet due: "Vence en 15 días" / "Vence hoy" / "3 días de atraso".
 */
export function dueDaysLabel(ageDays: number): string {
  const days = Math.abs(ageDays);
  const unit = days === 1 ? 'día' : 'días';
  if (ageDays < 0) return `Vence en ${days} ${unit}`;
  if (ageDays === 0) return 'Vence hoy';
  return `${days} ${unit} de atraso`;
}

/**
 * Order statuses surfaced on the "Ventas por estado" panel, in the same workflow
 * order the backend dashboard emits. The reports API always returns a count for
 * each of these keys (0 when none), so iterating this list gives a stable row set.
 */
export const DASHBOARD_ORDER_STATUS_ORDER: readonly OrderStatusType[] = [
  OrderStatus.SUBMITTED,
  OrderStatus.PENDING_APPROVAL,
  OrderStatus.APPROVED,
  OrderStatus.DISPATCHED,
  OrderStatus.DELIVERED,
  OrderStatus.CANCELLED,
];
